import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import { SingleSelectDropdown } from "@shared/ui/Dropdowns";
import { Textarea } from "@shared/ui/Inputs";
import AppModal from "@shared/ui/Modal";
import React, { useState } from "react";
import { useIntl } from "react-intl";

export interface BookDemoFormData {
  companySize: string;
  product: string;
  message: string;
}

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: BookDemoFormData) => Promise<boolean>;
}

const COMPANY_SIZE_OPTIONS = [
  { value: "0-1", label: "0-1" },
  { value: "1-10", label: "1-10" },
  { value: "10-100", label: "10-100" },
  { value: "100-500", label: "100-500" },
  { value: "500+", label: "500+" },
];

const PRODUCT_OPTIONS = [
  { value: "makula-field-service", label: "Makula Field Service" },
  { value: "makula-cmms", label: "Makula CMMS" },
  { value: "makula-industrial-ai", label: "Makula Industrial AI" },
];

export function BookDemoModal({
  isOpen,
  onClose,
  onSubmit,
}: BookDemoModalProps) {
  const { messages } = useIntl();
  const msg = messages.login?.["bookDemo"] as Record<string, string>;

  const [companySize, setCompanySize] = useState<
    { value: string; label: string }[]
  >([]);
  const [product, setProduct] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setCompanySize([]);
    setProduct([]);
    setMessage("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!onSubmit) return;
    setIsSubmitting(true);
    try {
      const success = await onSubmit({
        companySize: companySize[0]?.label,
        product: product[0]?.label,
        message,
      });
      if (success) {
        resetForm();
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppModal
      isOpen={isOpen}
      title={msg?.["title"] as string}
      width="!w-[400px]"
      handleClose={handleClose}
      handleSubmit={handleSubmit}
      updateButtonText={msg?.["title"] as string}
      disableUpdate={!companySize.length || !product.length || !message || isSubmitting}
      content={
        <div className="flex flex-col gap-lg">
          <BodyText size={BODY_TEXT_SIZES.SMALL} color="text-secondary">
            {msg["info"]}
          </BodyText>
          <SingleSelectDropdown
            keyId="book-demo-company-size"
            label={msg?.["companySizeLabel"] as string}
            placeholder={msg?.["companySizePlaceholder"] as string}
            options={COMPANY_SIZE_OPTIONS}
            values={companySize}
            onChange={setCompanySize}
          />
          <SingleSelectDropdown
            keyId="book-demo-product"
            label={msg?.["productLabel"] as string}
            placeholder={msg?.["productPlaceholder"] as string}
            options={PRODUCT_OPTIONS}
            values={product}
            onChange={setProduct}
          />
          <Textarea
            keyId="book-demo-message"
            label={msg?.["messageLabel"] as string}
            placeholder={msg?.["messagePlaceholder"] as string}
            value={message}
            maxLength={5000}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
      }
    />
  );
}
